import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

interface Meme {
  id: number;
  title: string;
  imageUrl: string;
  description: string | null;
  upvotes: number;
  createdAt: string;
  authorUsername: string;
}

export default function MemesPage() {
  const [memes, setMemes] = useState<Meme[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [description, setDescription] = useState('');
  const [formError, setFormError] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchMemes();
  }, []);

  const fetchMemes = async () => {
    try {
      const res = await api.get('/api/memes');
      setMemes(res.data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const handleUpvote = async (id: number) => {
    if (!user) { navigate('/login'); return; }
    try {
      const res = await api.post(`/api/memes/${id}/upvote`);
      setMemes((prev) => prev.map((m) => m.id === id ? { ...m, upvotes: res.data.upvotes } : m));
    } catch { /* ignore */ }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    try {
      await api.post('/api/memes', { title, imageUrl, description: description || null });
      setTitle('');
      setImageUrl('');
      setDescription('');
      setShowForm(false);
      fetchMemes();
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Fejl ved oprettelse.');
    }
  };

  if (loading) return <div className="page-loading">Indlæser memes... 🐘</div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>🎭 Memes</h1>
        {user && (
          <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? '✕ Annuller' : '+ Del et meme'}
          </button>
        )}
      </div>

      {showForm && (
        <form className="card form-card" onSubmit={handleSubmit}>
          <h2>Nyt meme</h2>
          <div className="form-group">
            <label>Titel</label>
            <input className="form-input" value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="Meme titel..." />
          </div>
          <div className="form-group">
            <label>Billed-URL</label>
            <input className="form-input" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} required placeholder="https://..." />
          </div>
          <div className="form-group">
            <label>Beskrivelse (valgfri)</label>
            <textarea className="form-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Beskriv dit meme..." rows={2} />
          </div>
          {formError && <div className="alert alert-error">{formError}</div>}
          <button type="submit" className="btn btn-primary">Del meme</button>
        </form>
      )}

      {memes.length === 0 ? (
        <div className="empty-state">
          <span>😶</span>
          <p>Ingen memes endnu. Vær den første til at dele et!</p>
        </div>
      ) : (
        <div className="memes-grid">
          {memes.map((meme) => (
            <div key={meme.id} className="card meme-card">
              <img src={meme.imageUrl} alt={meme.title} className="meme-image" onError={(e) => { (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x300?text=Billede+ikke+tilgængeligt'; }} />
              <div className="meme-body">
                <h3 className="meme-title">{meme.title}</h3>
                {meme.description && <p className="meme-desc">{meme.description}</p>}
                <div className="meme-footer">
                  <span className="meme-author">👤 {meme.authorUsername}</span>
                  <button className="upvote-btn" onClick={() => handleUpvote(meme.id)}>
                    👍 {meme.upvotes}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
